import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Trash2, 
  FileText, 
  ArrowLeft,
  Edit2,
  Save,
  X,
  FolderOpen,
  Hammer,
  RefreshCw,
  Receipt,
  ChevronDown,
  ChevronRight,
  Layers,
  Calendar as CalendarIcon,
  Building2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { BUSINESS_TYPES } from '../constants/businessTypes';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format, parseISO } from 'date-fns';
import { el } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImplementationTemplates = ({ user }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editName, setEditName] = useState('');
  
  // Expense category states
  const [expenseCategoryOptions, setExpenseCategoryOptions] = useState([]);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [selectedCategoryToAdd, setSelectedCategoryToAdd] = useState('');
  const [categoryDocuments, setCategoryDocuments] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // New document input for each category
  const [newDocInputs, setNewDocInputs] = useState({});
  
  // Custom category dialog
  const [showCustomCategoryDialog, setShowCustomCategoryDialog] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [customCategoryDocs, setCustomCategoryDocs] = useState(['']);
  
  // General document options
  const [generalDocOptions, setGeneralDocOptions] = useState([]);
  
  // Editing states
  const [editingCategoryName, setEditingCategoryName] = useState(null); // { key, value }
  const [editingDocName, setEditingDocName] = useState(null); // { categoryKey, docIndex, value } for expense docs
  const [editingGeneralDocName, setEditingGeneralDocName] = useState(null); // { index, value }
  const [editingCategoryPercentage, setEditingCategoryPercentage] = useState(null); // { key, value }
  
  // New general document
  const [newGeneralDoc, setNewGeneralDoc] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchExpenseCategoryOptions();
    fetchGeneralDocOptions();
  }, [user]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/implementation-templates?user_id=${user.user_id}`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης προτύπων');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseCategoryOptions = async () => {
    try {
      const response = await axios.get(`${API}/expense-category-options`);
      setExpenseCategoryOptions(response.data);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const fetchGeneralDocOptions = async () => {
    try {
      const response = await axios.get(`${API}/general-document-options`);
      setGeneralDocOptions(response.data);
    } catch (error) {
      console.error('Error fetching general docs:', error);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Εισάγετε όνομα προτύπου');
      return;
    }
    try {
      const response = await axios.post(`${API}/implementation-templates?user_id=${user.user_id}`, {
        name: newTemplateName,
        documents: [],
        expense_categories: []
      });
      setTemplates([...templates, response.data]);
      setNewTemplateName('');
      setShowNewTemplateDialog(false);
      setSelectedTemplate(response.data);
      toast.success('Το πρότυπο δημιουργήθηκε');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας προτύπου');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το πρότυπο;')) return;
    try {
      await axios.delete(`${API}/implementation-templates/${templateId}`);
      setTemplates(templates.filter(t => t.template_id !== templateId));
      if (selectedTemplate?.template_id === templateId) {
        setSelectedTemplate(null);
      }
      toast.success('Το πρότυπο διαγράφηκε');
    } catch (error) {
      toast.error('Σφάλμα διαγραφής προτύπου');
    }
  };

  const handleInitDocuments = async () => {
    if (!selectedTemplate) return;
    
    if (selectedTemplate.documents?.length > 0) {
      if (!window.confirm('Τα υπάρχοντα δικαιολογητικά θα αντικατασταθούν. Συνέχεια;')) return;
    }
    
    try {
      const response = await axios.post(`${API}/implementation-templates/${selectedTemplate.template_id}/init-documents`);
      toast.success(response.data.message);
      await refreshSelectedTemplate();
    } catch (error) {
      toast.error('Σφάλμα αρχικοποίησης');
    }
  };

  const refreshSelectedTemplate = async () => {
    if (!selectedTemplate) return;
    const updated = await axios.get(`${API}/implementation-templates/${selectedTemplate.template_id}`);
    setSelectedTemplate(updated.data);
    setTemplates(templates.map(t => 
      t.template_id === updated.data.template_id ? updated.data : t
    ));
  };

  // When user selects a category to add, load its default documents
  const handleCategorySelectionChange = (categoryKey) => {
    setSelectedCategoryToAdd(categoryKey);
    if (categoryKey) {
      const catOption = expenseCategoryOptions.find(c => c.key === categoryKey);
      // Fetch full category details with documents
      axios.get(`${API}/expense-categories`).then(response => {
        const fullCat = response.data.find(c => c.key === categoryKey);
        if (fullCat) {
          setCategoryDocuments(fullCat.documents || []);
        }
      });
    } else {
      setCategoryDocuments([]);
    }
  };

  // Add expense category with selected documents
  const handleAddExpenseCategory = async () => {
    if (!selectedTemplate || !selectedCategoryToAdd) return;
    
    const categoryData = expenseCategoryOptions.find(c => c.key === selectedCategoryToAdd);
    if (!categoryData) return;
    
    // Check if already exists
    if (selectedTemplate.expense_categories?.some(ec => ec.key === selectedCategoryToAdd)) {
      toast.error('Η κατηγορία υπάρχει ήδη στο πρότυπο');
      return;
    }
    
    // Add to template with the selected documents
    const updatedCategories = [
      ...(selectedTemplate.expense_categories || []),
      {
        key: categoryData.key,
        name: categoryData.name,
        documents: categoryDocuments
      }
    ];
    
    try {
      const response = await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        expense_categories: updatedCategories
      });
      const projectsUpdated = response.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Η κατηγορία προστέθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Η κατηγορία προστέθηκε'
      );
      await refreshSelectedTemplate();
      setShowAddCategoryDialog(false);
      setSelectedCategoryToAdd('');
      setCategoryDocuments([]);
    } catch (error) {
      toast.error('Σφάλμα προσθήκης κατηγορίας');
    }
  };

  // Add custom category
  const handleAddCustomCategory = async () => {
    if (!selectedTemplate || !customCategoryName.trim()) {
      toast.error('Εισάγετε όνομα κατηγορίας');
      return;
    }
    
    const filteredDocs = customCategoryDocs.filter(d => d.trim() !== '');
    if (filteredDocs.length === 0) {
      toast.error('Προσθέστε τουλάχιστον ένα δικαιολογητικό');
      return;
    }
    
    // Generate a key from the name
    const key = customCategoryName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    // Check if key already exists
    if (selectedTemplate.expense_categories?.some(ec => ec.key === key)) {
      toast.error('Η κατηγορία υπάρχει ήδη');
      return;
    }
    
    const updatedCategories = [
      ...(selectedTemplate.expense_categories || []),
      {
        key: key,
        name: customCategoryName,
        documents: filteredDocs
      }
    ];
    
    try {
      const response = await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        expense_categories: updatedCategories
      });
      const projectsUpdated = response.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Η κατηγορία προστέθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Η κατηγορία προστέθηκε'
      );
      await refreshSelectedTemplate();
      setShowCustomCategoryDialog(false);
      setCustomCategoryName('');
      setCustomCategoryDocs(['']);
    } catch (error) {
      toast.error('Σφάλμα προσθήκης κατηγορίας');
    }
  };

  // Remove expense category from template
  const handleRemoveExpenseCategory = async (categoryKey) => {
    if (!selectedTemplate) return;
    
    const updatedCategories = selectedTemplate.expense_categories.filter(ec => ec.key !== categoryKey);
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, {
        expense_categories: updatedCategories
      });
      toast.success('Η κατηγορία αφαιρέθηκε');
      await refreshSelectedTemplate();
    } catch (error) {
      toast.error('Σφάλμα αφαίρεσης κατηγορίας');
    }
  };

  // Add document to a category
  const handleAddDocToCategory = async (categoryKey) => {
    const newDoc = newDocInputs[categoryKey]?.trim();
    if (!newDoc) return;
    
    const updatedCategories = selectedTemplate.expense_categories.map(cat => {
      if (cat.key === categoryKey) {
        return {
          ...cat,
          documents: [...(cat.documents || []), newDoc]
        };
      }
      return cat;
    });
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, {
        expense_categories: updatedCategories
      });
      setNewDocInputs({ ...newDocInputs, [categoryKey]: '' });
      await refreshSelectedTemplate();
      toast.success('Το δικαιολογητικό προστέθηκε');
    } catch (error) {
      toast.error('Σφάλμα προσθήκης');
    }
  };

  // Remove document from a category
  const handleRemoveDocFromCategory = async (categoryKey, docIndex) => {
    const updatedCategories = selectedTemplate.expense_categories.map(cat => {
      if (cat.key === categoryKey) {
        return {
          ...cat,
          documents: cat.documents.filter((_, i) => i !== docIndex)
        };
      }
      return cat;
    });
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, {
        expense_categories: updatedCategories
      });
      await refreshSelectedTemplate();
      toast.success('Το δικαιολογητικό αφαιρέθηκε');
    } catch (error) {
      toast.error('Σφάλμα αφαίρεσης');
    }
  };

  // Remove general document from template
  const handleRemoveDocument = async (docIndex) => {
    if (!selectedTemplate) return;
    
    const updatedDocs = selectedTemplate.documents.filter((_, i) => i !== docIndex);
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, {
        documents: updatedDocs
      });
      await refreshSelectedTemplate();
      toast.success('Το δικαιολογητικό αφαιρέθηκε');
    } catch (error) {
      toast.error('Σφάλμα αφαίρεσης');
    }
  };

  const handleToggleImplBusinessType = async (docIndex, businessType) => {
    if (!selectedTemplate) return;
    const docs = [...selectedTemplate.documents];
    const doc = { ...docs[docIndex] };
    const current = doc.business_types || [];
    if (current.includes(businessType)) {
      doc.business_types = current.filter(bt => bt !== businessType);
    } else {
      doc.business_types = [...current, businessType];
    }
    docs[docIndex] = doc;
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, { documents: docs });
      await refreshSelectedTemplate();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const startEditing = (template) => {
    setEditingTemplate(template.template_id);
    setEditName(template.name);
  };

  const saveTemplateName = async (templateId) => {
    try {
      await axios.put(`${API}/implementation-templates/${templateId}`, {
        name: editName
      });
      setTemplates(templates.map(t => 
        t.template_id === templateId ? { ...t, name: editName } : t
      ));
      if (selectedTemplate?.template_id === templateId) {
        setSelectedTemplate({ ...selectedTemplate, name: editName });
      }
      setEditingTemplate(null);
      toast.success('Το όνομα ενημερώθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // Get category name from options
  const getCategoryName = (key) => {
    const cat = generalDocOptions.find(c => c.key === key);
    return cat?.name || key;
  };

  // Get available categories to add (not already in template)
  const getAvailableCategories = () => {
    const existingKeys = selectedTemplate?.expense_categories?.map(ec => ec.key) || [];
    return expenseCategoryOptions.filter(opt => !existingKeys.includes(opt.key));
  };

  // Remove document from category selection dialog
  const handleRemoveDocFromSelection = (index) => {
    setCategoryDocuments(categoryDocuments.filter((_, i) => i !== index));
  };

  // Edit category name
  const handleSaveCategoryName = async (categoryKey) => {
    if (!editingCategoryName || !editingCategoryName.value.trim()) {
      setEditingCategoryName(null);
      return;
    }
    
    const updatedCategories = selectedTemplate.expense_categories.map(cat => {
      if (cat.key === categoryKey) {
        return { ...cat, name: editingCategoryName.value };
      }
      return cat;
    });
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        expense_categories: updatedCategories
      });
      await refreshSelectedTemplate();
      setEditingCategoryName(null);
      toast.success('Το όνομα ενημερώθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Edit category max percentage
  const handleSaveCategoryPercentage = async (categoryKey) => {
    const percentage = parseFloat(editingCategoryPercentage?.value) || 0;
    
    const updatedCategories = selectedTemplate.expense_categories.map(cat => {
      if (cat.key === categoryKey) {
        return { ...cat, max_percentage: percentage };
      }
      return cat;
    });
    
    try {
      const response = await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        expense_categories: updatedCategories
      });
      await refreshSelectedTemplate();
      setEditingCategoryPercentage(null);
      const projectsUpdated = response.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Το όριο ενημερώθηκε σε ${projectsUpdated} έργα`
          : 'Το όριο ενημερώθηκε'
      );
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Edit document name in expense category
  const handleSaveDocName = async (categoryKey, docIndex) => {
    if (!editingDocName || !editingDocName.value.trim()) {
      setEditingDocName(null);
      return;
    }
    
    const updatedCategories = selectedTemplate.expense_categories.map(cat => {
      if (cat.key === categoryKey) {
        const newDocs = [...cat.documents];
        newDocs[docIndex] = editingDocName.value;
        return { ...cat, documents: newDocs };
      }
      return cat;
    });
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        expense_categories: updatedCategories
      });
      await refreshSelectedTemplate();
      setEditingDocName(null);
      toast.success('Το όνομα ενημερώθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Edit general document name
  const handleSaveGeneralDocName = async (docIndex) => {
    if (!editingGeneralDocName || !editingGeneralDocName.value.trim()) {
      setEditingGeneralDocName(null);
      return;
    }
    
    const updatedDocs = selectedTemplate.documents.map((doc, i) => {
      if (i === docIndex) {
        return { ...doc, document_name: editingGeneralDocName.value };
      }
      return doc;
    });
    
    try {
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        documents: updatedDocs
      });
      await refreshSelectedTemplate();
      setEditingGeneralDocName(null);
      toast.success('Το όνομα ενημερώθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // Add general document
  const handleAddGeneralDocument = async () => {
    if (!newGeneralDoc.trim()) return;
    
    const newDoc = {
      document_name: newGeneralDoc.trim(),
      category: 'general'
    };
    
    const updatedDocs = [...(selectedTemplate.documents || []), newDoc];
    
    try {
      const response = await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}?sync_projects=true`, {
        documents: updatedDocs
      });
      await refreshSelectedTemplate();
      setNewGeneralDoc('');
      const projectsUpdated = response.data.projects_updated || 0;
      toast.success(
        projectsUpdated > 0
          ? `Το δικαιολογητικό προστέθηκε σε ${projectsUpdated} συνδεδεμένα έργα`
          : 'Το δικαιολογητικό προστέθηκε'
      );
    } catch (error) {
      toast.error('Σφάλμα προσθήκης');
    }
  };

  // Update template deadline
  const handleUpdateDeadline = async (date) => {
    if (!selectedTemplate) return;
    
    try {
      const deadlineStr = date ? date.toISOString() : "";  // Empty string to clear
      await axios.put(`${API}/implementation-templates/${selectedTemplate.template_id}`, {
        deadline: deadlineStr
      });
      setSelectedTemplate({ ...selectedTemplate, deadline: date ? deadlineStr : null });
      setTemplates(templates.map(t => 
        t.template_id === selectedTemplate.template_id ? { ...t, deadline: date ? deadlineStr : null } : t
      ));
      toast.success(date ? 'Η προθεσμία ενημερώθηκε' : 'Η προθεσμία αφαιρέθηκε');
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης προθεσμίας');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/select-module')}
              className="text-slate-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <div className="flex items-center gap-2">
              <Hammer className="h-6 w-6 text-emerald-600" />
              <h1 className="text-xl font-bold text-slate-900">Πρότυπα Υλοποίησης</h1>
            </div>
          </div>
          <Button
            onClick={() => setShowNewTemplateDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="new-impl-template-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Νέο Πρότυπο
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Layers className="h-5 w-5 text-emerald-600" />
                Τα Πρότυπά μου
              </h2>
              
              {templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Δεν υπάρχουν πρότυπα</p>
                  <p className="text-sm">Δημιουργήστε ένα νέο πρότυπο</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.template_id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTemplate?.template_id === template.template_id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-300 bg-white'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                      data-testid={`impl-template-${template.template_id}`}
                    >
                      <div className="flex items-center justify-between">
                        {editingTemplate === template.template_id ? (
                          <div className="flex items-center gap-2 flex-1 mr-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="h-8 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                saveTemplateName(template.template_id);
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <Save className="h-4 w-4 text-emerald-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTemplate(null);
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1">
                              <h3 className="font-medium text-slate-900 text-sm">{template.name}</h3>
                              <div className="flex gap-3 text-xs text-slate-500 mt-1">
                                <span>{template.document_count || 0} δικ/κά</span>
                                <span>{template.expense_categories?.length || 0} κατηγορίες</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditing(template);
                                }}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-emerald-600"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(template.template_id);
                                }}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Template Details */}
          <div className="lg:col-span-2">
            {selectedTemplate ? (
              <div className="space-y-6">
                {/* Header with Actions */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">
                      {selectedTemplate.name}
                    </h2>
                    <Button
                      onClick={handleInitDocuments}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="init-docs-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Αρχικοποίηση Γενικών Δικαιολογητικών
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500">
                    Πατήστε "Αρχικοποίηση" για να προστεθούν αυτόματα τα βασικά γενικά δικαιολογητικά υλοποίησης.
                  </p>
                </div>

                {/* Deadline Section */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                        <CalendarIcon className="h-5 w-5 text-amber-600" />
                        Προεπιλεγμένη Προθεσμία
                      </h3>
                      <p className="text-sm text-slate-500">
                        Τα νέα έργα που δημιουργούνται από αυτό το πρότυπο θα έχουν αυτή την προθεσμία.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-[200px] justify-start text-left font-normal ${!selectedTemplate.deadline && "text-muted-foreground"}`}
                            data-testid="template-deadline-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedTemplate.deadline
                              ? format(parseISO(selectedTemplate.deadline), 'dd/MM/yyyy', { locale: el })
                              : "Επιλογή ημερομηνίας"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                          <Calendar
                            mode="single"
                            selected={selectedTemplate.deadline ? parseISO(selectedTemplate.deadline) : undefined}
                            onSelect={handleUpdateDeadline}
                            initialFocus
                            locale={el}
                          />
                        </PopoverContent>
                      </Popover>
                      {selectedTemplate.deadline && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleUpdateDeadline(null)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid="clear-deadline-btn"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* General Documents Section */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-emerald-600" />
                      Γενικά Δικαιολογητικά ({selectedTemplate.documents?.length || 0})
                    </h3>
                  </div>

                  {/* Add new general document */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={newGeneralDoc}
                      onChange={(e) => setNewGeneralDoc(e.target.value)}
                      placeholder="Νέο γενικό δικαιολογητικό..."
                      onKeyDown={(e) => e.key === 'Enter' && handleAddGeneralDocument()}
                    />
                    <Button
                      size="sm"
                      onClick={handleAddGeneralDocument}
                      disabled={!newGeneralDoc.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {selectedTemplate.documents?.length > 0 ? (
                    <div className="space-y-4">
                      {Object.entries(
                        selectedTemplate.documents.reduce((acc, doc, index) => {
                          const cat = doc.category || 'general';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push({ ...doc, _index: index });
                          return acc;
                        }, {})
                      ).map(([category, docs]) => (
                        <div key={category} className="border border-slate-200 rounded-lg p-4">
                          <h4 className="font-medium text-slate-700 mb-3">{getCategoryName(category)}</h4>
                          <div className="space-y-2">
                            {docs.map((doc) => (
                              <div
                                key={doc._index}
                                className="p-2 bg-slate-50 rounded border border-slate-100"
                              >
                                <div className="flex items-center justify-between">
                                {editingGeneralDocName?.index === doc._index ? (
                                  <div className="flex items-center gap-2 flex-1 mr-2">
                                    <Input
                                      value={editingGeneralDocName.value}
                                      onChange={(e) => setEditingGeneralDocName({ ...editingGeneralDocName, value: e.target.value })}
                                      className="h-7 text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveGeneralDocName(doc._index);
                                        if (e.key === 'Escape') setEditingGeneralDocName(null);
                                      }}
                                    />
                                    <Button size="sm" variant="ghost" onClick={() => handleSaveGeneralDocName(doc._index)} className="h-7 w-7 p-0 text-emerald-600">
                                      <Save className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingGeneralDocName(null)} className="h-7 w-7 p-0 text-slate-400">
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="text-sm text-slate-700">{doc.document_name}</span>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setEditingGeneralDocName({ index: doc._index, value: doc.document_name })}
                                        className="h-7 w-7 p-0 text-slate-400 hover:text-cyan-600"
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleRemoveDocument(doc._index)}
                                        className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                              {/* Business Types Toggle */}
                              <div className="ml-8 mt-1">
                                <details className="group">
                                  <summary className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                                    <Building2 className="h-3 w-3" />
                                    {doc.business_types?.length > 0
                                      ? <span className="text-blue-700">{doc.business_types.length} μορφές</span>
                                      : <span>Όλες οι μορφές</span>
                                    }
                                    <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                                  </summary>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {BUSINESS_TYPES.map(bt => {
                                      const isSelected = (doc.business_types || []).includes(bt);
                                      return (
                                        <button
                                          key={bt}
                                          onClick={() => handleToggleImplBusinessType(doc._index, bt)}
                                          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                                            isSelected
                                              ? 'bg-blue-100 border-blue-300 text-blue-800'
                                              : 'bg-white border-slate-200 text-slate-500 hover:border-blue-200 hover:text-blue-700'
                                          }`}
                                        >
                                          {bt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </details>
                              </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Δεν υπάρχουν γενικά δικαιολογητικά</p>
                      <p className="text-sm">Πατήστε "Αρχικοποίηση" ή προσθέστε χειροκίνητα</p>
                    </div>
                  )}
                </div>

                {/* Expense Categories Section */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-amber-600" />
                      Κατηγορίες Δαπανών ({selectedTemplate.expense_categories?.length || 0})
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowCustomCategoryDialog(true)}
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Νέα Κατηγορία
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddCategoryDialog(true)}
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        disabled={getAvailableCategories().length === 0}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Από Πρότυπο
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-500 mb-4">
                    Οι κατηγορίες δαπανών καθορίζουν τα δικαιολογητικά που απαιτούνται για κάθε τύπο τιμολογίου.
                  </p>

                  {selectedTemplate.expense_categories?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedTemplate.expense_categories.map((cat) => (
                        <div key={cat.key} className="border border-amber-200 rounded-lg overflow-hidden">
                          {/* Category Header */}
                          <div 
                            className="flex items-center justify-between p-3 bg-amber-50 cursor-pointer"
                            onClick={() => toggleCategory(cat.key)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              {expandedCategories[cat.key] ? (
                                <ChevronDown className="h-4 w-4 text-amber-600" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-amber-600" />
                              )}
                              {editingCategoryName?.key === cat.key ? (
                                <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                                  <Input
                                    value={editingCategoryName.value}
                                    onChange={(e) => setEditingCategoryName({ ...editingCategoryName, value: e.target.value })}
                                    className="h-7 text-sm flex-1"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveCategoryName(cat.key);
                                      if (e.key === 'Escape') setEditingCategoryName(null);
                                    }}
                                  />
                                  <Button size="sm" variant="ghost" onClick={() => handleSaveCategoryName(cat.key)} className="h-7 w-7 p-0 text-emerald-600">
                                    <Save className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingCategoryName(null)} className="h-7 w-7 p-0 text-slate-400">
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <span className="font-medium text-slate-700">{cat.name}</span>
                                  <span className="text-xs text-slate-500">
                                    ({cat.documents?.length || 0} δικαιολογητικά)
                                  </span>
                                  {/* Max Percentage Badge */}
                                  {editingCategoryPercentage?.key === cat.key ? (
                                    <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={editingCategoryPercentage.value}
                                        onChange={(e) => setEditingCategoryPercentage({ ...editingCategoryPercentage, value: e.target.value })}
                                        className="h-6 w-16 text-xs"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveCategoryPercentage(cat.key);
                                          if (e.key === 'Escape') setEditingCategoryPercentage(null);
                                        }}
                                      />
                                      <span className="text-xs">%</span>
                                      <Button size="sm" variant="ghost" onClick={() => handleSaveCategoryPercentage(cat.key)} className="h-5 w-5 p-0 text-emerald-600">
                                        <Save className="h-3 w-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingCategoryPercentage(null)} className="h-5 w-5 p-0 text-slate-400">
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingCategoryPercentage({ key: cat.key, value: cat.max_percentage || '' });
                                      }}
                                      className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                        cat.max_percentage 
                                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                      }`}
                                    >
                                      {cat.max_percentage ? `≤${cat.max_percentage}%` : 'Όριο %'}
                                    </button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCategoryName({ key: cat.key, value: cat.name });
                                    }}
                                    className="h-6 w-6 p-0 text-slate-400 hover:text-cyan-600 ml-1"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveExpenseCategory(cat.key);
                              }}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          
                          {/* Category Documents (expandable) */}
                          {expandedCategories[cat.key] && (
                            <div className="p-3 bg-white border-t border-amber-200">
                              <div className="space-y-2 mb-3">
                                {cat.documents?.map((doc, docIndex) => (
                                  <div
                                    key={docIndex}
                                    className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100"
                                  >
                                    {editingDocName?.categoryKey === cat.key && editingDocName?.docIndex === docIndex ? (
                                      <div className="flex items-center gap-2 flex-1 mr-2">
                                        <Input
                                          value={editingDocName.value}
                                          onChange={(e) => setEditingDocName({ ...editingDocName, value: e.target.value })}
                                          className="h-7 text-sm"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveDocName(cat.key, docIndex);
                                            if (e.key === 'Escape') setEditingDocName(null);
                                          }}
                                        />
                                        <Button size="sm" variant="ghost" onClick={() => handleSaveDocName(cat.key, docIndex)} className="h-7 w-7 p-0 text-emerald-600">
                                          <Save className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingDocName(null)} className="h-7 w-7 p-0 text-slate-400">
                                          <X className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-sm text-slate-700">{doc}</span>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setEditingDocName({ categoryKey: cat.key, docIndex, value: doc })}
                                            className="h-6 w-6 p-0 text-slate-400 hover:text-cyan-600"
                                          >
                                            <Edit2 className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleRemoveDocFromCategory(cat.key, docIndex)}
                                            className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600"
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {/* Add new document */}
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Νέο δικαιολογητικό..."
                                  value={newDocInputs[cat.key] || ''}
                                  onChange={(e) => setNewDocInputs({ ...newDocInputs, [cat.key]: e.target.value })}
                                  className="flex-1 h-8 text-sm"
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddDocToCategory(cat.key)}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleAddDocToCategory(cat.key)}
                                  className="h-8 bg-amber-500 hover:bg-amber-600"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                      <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Δεν έχουν οριστεί κατηγορίες δαπανών</p>
                      <p className="text-sm">Προσθέστε κατηγορίες για τα τιμολόγια</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-6 text-center text-slate-500">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Επιλέξτε ένα πρότυπο</h3>
                <p className="text-sm">ή δημιουργήστε ένα νέο για να ξεκινήσετε</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Template Dialog */}
      <Dialog open={showNewTemplateDialog} onOpenChange={setShowNewTemplateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Νέο Πρότυπο Υλοποίησης</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Όνομα Προτύπου</Label>
              <Input
                placeholder="π.χ. ΕΣΠΑ Ψηφιακός Μετασχηματισμός"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTemplate()}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewTemplateDialog(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handleCreateTemplate} className="bg-emerald-600 hover:bg-emerald-700">
                Δημιουργία
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category from Template Dialog */}
      <Dialog open={showAddCategoryDialog} onOpenChange={(open) => {
        setShowAddCategoryDialog(open);
        if (!open) {
          setSelectedCategoryToAdd('');
          setCategoryDocuments([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Προσθήκη Κατηγορίας από Πρότυπο</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Επιλέξτε Κατηγορία</Label>
              <select
                value={selectedCategoryToAdd}
                onChange={(e) => handleCategorySelectionChange(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm"
              >
                <option value="">-- Επιλέξτε --</option>
                {getAvailableCategories().map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.name} ({cat.document_count} δικαιολογητικά)
                  </option>
                ))}
              </select>
            </div>
            
            {/* Show documents of selected category */}
            {categoryDocuments.length > 0 && (
              <div className="space-y-2">
                <Label>Δικαιολογητικά (μπορείτε να αφαιρέσετε)</Label>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                  {categoryDocuments.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                    >
                      <span>{doc}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveDocFromSelection(index)}
                        className="h-6 w-6 p-0 text-slate-400 hover:text-rose-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddCategoryDialog(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleAddExpenseCategory} 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!selectedCategoryToAdd || categoryDocuments.length === 0}
              >
                Προσθήκη
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Category Dialog */}
      <Dialog open={showCustomCategoryDialog} onOpenChange={(open) => {
        setShowCustomCategoryDialog(open);
        if (!open) {
          setCustomCategoryName('');
          setCustomCategoryDocs(['']);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Νέα Κατηγορία Δαπανών</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Όνομα Κατηγορίας</Label>
              <Input
                placeholder="π.χ. Εξοπλισμός Γραφείου"
                value={customCategoryName}
                onChange={(e) => setCustomCategoryName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Δικαιολογητικά</Label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {customCategoryDocs.map((doc, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Όνομα δικαιολογητικού"
                      value={doc}
                      onChange={(e) => {
                        const newDocs = [...customCategoryDocs];
                        newDocs[index] = e.target.value;
                        setCustomCategoryDocs(newDocs);
                      }}
                      className="flex-1"
                    />
                    {customCategoryDocs.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCustomCategoryDocs(customCategoryDocs.filter((_, i) => i !== index))}
                        className="text-slate-400 hover:text-rose-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCustomCategoryDocs([...customCategoryDocs, ''])}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Προσθήκη Δικαιολογητικού
              </Button>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCustomCategoryDialog(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleAddCustomCategory} 
                className="bg-amber-500 hover:bg-amber-600"
                disabled={!customCategoryName.trim()}
              >
                Δημιουργία
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImplementationTemplates;
